
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function InsurancePlanDetails() {
  const titleRef = useScrollAnimation();
  
  // Create refs for all plan cards at the top level
  const planRefs = [
    useScrollAnimation(),
    useScrollAnimation(),
    useScrollAnimation(),
    useScrollAnimation()
  ];

  const plans = [
    {
      name: 'Individual & Family Plans',
      icon: 'ri-user-heart-line',
      description: 'Comprehensive health insurance coverage designed for individuals and families who need flexible, personalized protection.',
      benefits: [
        'Comprehensive medical coverage including doctor visits, hospital stays, and surgeries',
        'Prescription drug coverage with nationwide pharmacy network',
        'Preventive care services at no additional cost',
        'Mental health and substance abuse treatment',
        'Maternity and newborn care',
        'Pediatric services including dental and vision for children'
      ],
      whoItsFor: [
        'Self-employed individuals and freelancers',
        'Families without employer-sponsored coverage',
        'Early retirees not yet eligible for Medicare',
        'Anyone seeking comprehensive health protection'
      ],
      keyFeatures: [
        'Choice of multiple carriers and plan designs',
        'Flexible deductibles and copayment options',
        'Nationwide provider networks',
        'ACA-compliant plans with essential health benefits'
      ]
    },
    {
      name: 'Group & Employer Plans',
      icon: 'ri-team-line',
      description: 'Cost-effective health insurance solutions for businesses of all sizes, helping you attract and retain top talent.',
      benefits: [
        'Lower premiums through group purchasing power',
        'Employer contribution reduces employee costs',
        'Multiple plan options to fit different budgets',
        'Comprehensive coverage including medical, dental, and vision',
        'Wellness programs and preventive care incentives',
        'Easy enrollment and administration support'
      ],
      whoItsFor: [
        'Small businesses with 2-50 employees',
        'Mid-sized companies with 51-500 employees',
        'Large corporations with 500+ employees',
        'Business owners looking to provide employee benefits'
      ],
      keyFeatures: [
        'Competitive rates from top-rated carriers',
        'Flexible plan designs and contribution strategies',
        'Online enrollment and management tools',
        'Dedicated account management and support'
      ]
    },
    {
      name: 'Medicare Supplement Plans',
      icon: 'ri-heart-pulse-line',
      description: 'Fill the gaps in Original Medicare with supplemental coverage that provides peace of mind and financial protection.',
      benefits: [
        'Covers Medicare Part A and Part B deductibles and copayments',
        'No network restrictions - see any doctor who accepts Medicare',
        'Guaranteed renewable coverage',
        'Travel emergency coverage outside the United States',
        'Predictable out-of-pocket costs',
        'No referrals needed to see specialists'
      ],
      whoItsFor: [
        'Individuals age 65 and older enrolled in Medicare',
        'People under 65 with certain disabilities',
        'Those seeking predictable healthcare costs',
        'Frequent travelers who want nationwide coverage'
      ],
      keyFeatures: [
        'Standardized plans (A through N) for easy comparison',
        'Guaranteed issue during open enrollment period',
        'No prior authorization required for services',
        'Freedom to choose any Medicare-accepting provider'
      ]
    },
    {
      name: 'Short-Term Health Insurance',
      icon: 'ri-time-line',
      description: 'Temporary health insurance coverage for gaps in coverage, providing essential protection when you need it most.',
      benefits: [
        'Quick approval and immediate coverage',
        'Lower premiums compared to traditional plans',
        'Flexible coverage periods from 30 days to 12 months',
        'Emergency and urgent care coverage',
        'Doctor visits and specialist consultations',
        'Prescription drug benefits'
      ],
      whoItsFor: [
        'Individuals between jobs or waiting for employer coverage',
        'Recent college graduates aging off parents\' plans',
        'Early retirees before Medicare eligibility',
        'Seasonal workers needing temporary coverage'
      ],
      keyFeatures: [
        'Fast application and approval process',
        'Customizable deductibles and coverage limits',
        'Renewable options in most states',
        'Nationwide provider networks'
      ]
    }
  ];

  return (
    <section className="py-20 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-4">
            Detailed Plan Information
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Learn more about each insurance plan type and find the perfect fit for your needs
          </p>
        </div>

        <div className="space-y-16">
          {plans.map((plan, index) => {
            const cardRef = planRefs[index];
            
            return (
              <div key={index} className="bg-gray-50 rounded-lg p-8 md:p-12">
                <div 
                  ref={cardRef.ref}
                  className={`flex items-center gap-4 mb-6 transition-all duration-1000 ${
                    cardRef.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                  }`}
                >
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                    <i className={`${plan.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-3xl font-serif font-bold text-primary">{plan.name}</h3>
                </div>

                <p className="text-lg text-gray-700 mb-8">{plan.description}</p>

                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-xl font-semibold text-primary mb-4 flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-accent"></i>
                      Key Benefits
                    </h4>
                    <ul className="space-y-3">
                      {plan.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm text-gray-700">
                          <i className="ri-check-line text-accent text-lg mt-0.5 flex-shrink-0"></i>
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-primary mb-4 flex items-center gap-2">
                      <i className="ri-user-line text-accent"></i>
                      Who It's For
                    </h4>
                    <ul className="space-y-3 mb-6">
                      {plan.whoItsFor.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm text-gray-700">
                          <i className="ri-arrow-right-s-line text-accent text-lg mt-0.5 flex-shrink-0"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    <h4 className="text-xl font-semibold text-primary mb-4 flex items-center gap-2">
                      <i className="ri-star-line text-accent"></i>
                      Key Features
                    </h4>
                    <ul className="space-y-3">
                      {plan.keyFeatures.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm text-gray-700">
                          <i className="ri-arrow-right-s-line text-accent text-lg mt-0.5 flex-shrink-0"></i>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-gray-300">
                  <a
                    href="/contact"
                    className="inline-flex items-center gap-2 bg-accent text-white px-8 py-3 rounded-lg font-semibold hover:bg-accent/90 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Get a Free Quote
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
